import { css } from '@emotion/react';
import { t } from 'i18n';
import { PrepaidBaggageAncillary, SpecialAssistance } from 'models';

import { SasIcon } from '../SasIcon';
import { theme } from '../theme';

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.md};
  `,
  rebookingFeeMessage: css`
    display: flex;
    align-items: center;
    margin: 0 0 ${theme.spacing.md} 0;
    gap: ${theme.spacing.xs};
  `,
  conditionSection: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.sm};
  `,
  conditionsListItem: css`
    display: flex;
    align-items: baseline;
    font-size: ${theme.fontSize.sm};
  `,
  itemIcon: css`
    margin-right: ${theme.spacing.xs};
  `,
  coralDot: css`
    background-color: var(--color-coral-1);
    width: 7px;
    height: 7px;
    border-radius: 50%;
    margin-right: 6px;
  `,
};

interface ConditionListProps {
  cancellationFee: FeeStatus;
  rebookingFee: FeeStatus;
  prepaidBaggage: PrepaidBaggageAncillary | undefined;
  specialAssistance: SpecialAssistance | undefined;
}

const buildSpecialAssistanceLabel = (specialAssistance: SpecialAssistance) => {
  const items = [];
  if (specialAssistance?.hearingAssistance) {
    items.push(t('rebooking.transferred.hearingAssistance'));
  }
  if (specialAssistance?.serviceAnimal) {
    items.push(t('rebooking.transferred.serviceAnimal'));
  }
  if (specialAssistance?.wheelChair) {
    items.push(t('rebooking.transferred.wheelChair'));
  }
  return t('rebooking.transferred.assistance', {
    items: items.join(' & '),
    defaultValue: 'will be transferred to new flight {{items}}',
  });
};

export const enum FeeStatus {
  FREE = 'FREE',
  FEE = 'FEE',
  UNKNOWN = 'UNKNOWN',
}

export const ConditionList = ({
  cancellationFee,
  rebookingFee,
  prepaidBaggage,
  specialAssistance,
}: ConditionListProps) => {
  const hasSpecialAssistance = Boolean(
    specialAssistance?.hearingAssistance || specialAssistance?.serviceAnimal || specialAssistance?.wheelChair
  );

  const ruleItems = [
    ...(rebookingFee !== FeeStatus.UNKNOWN
      ? [
          {
            icon: 'cl--calendar-plane',
            label: rebookingFee === FeeStatus.FEE ? t('rebookingFeeWillApply') : t('rebookingWithoutFee'),
          },
        ]
      : []),
    ...(cancellationFee === FeeStatus.FREE
      ? [
          {
            icon: 'cl--calendar-arrow',
            label: t('freeCancelation', { defaultValue: 'Free cancelation' }),
          },
        ]
      : []),
  ];
  const infoItems = [
    {
      icon: 'cl--clock',
      label: t('rebookableUntil'),
    },
    ...(prepaidBaggage?.noOfCabin
      ? [{ icon: 'cl--baggage', label: t('transferred.carryOnBag', { count: prepaidBaggage.noOfCabin }) }]
      : []),
    ...(!!prepaidBaggage?.noOfRegularBaggages || !!prepaidBaggage?.noOfSpecialBaggages
      ? [
          {
            icon: 'cl--baggage-large',
            label: t('transferred.checkInBag', {
              count: prepaidBaggage?.noOfRegularBaggages + prepaidBaggage?.noOfSpecialBaggages,
            }),
          },
        ]
      : []),
    ...(hasSpecialAssistance && specialAssistance
      ? [{ icon: 'cl--assistance', label: buildSpecialAssistanceLabel(specialAssistance) }]
      : []),
  ];

  return (
    <div css={styles.container}>
      {ruleItems.length > 0 && (
        <div css={styles.conditionSection}>
          {ruleItems.map(({ icon, label }, index) => (
            <span css={styles.conditionsListItem} key={index}>
              <SasIcon css={styles.itemIcon} name={icon} />
              {label}
            </span>
          ))}
        </div>
      )}
      <div css={styles.conditionSection}>
        {infoItems.map(({ icon, label }, index) => (
          <span css={styles.conditionsListItem} key={index}>
            <SasIcon css={styles.itemIcon} name={icon} />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
};
